import { htmlEscape } from "../../utils/vanilla";
import Translator from "../../utils/translator";
import DefectListTranslation from "../../utils/defectListTranslation";
import entityUtil from "../../utils/entity";
import DVIRLog, { IDvirDefects } from "../../model/DVIRLog";
import { getDefectListsForAssetType } from "../../utils/defectList";
import Api from "../../services/api";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { getCurrentVehicle } from "../../utils/device";
import { makeDeviceCache } from "../../utils/storage/cache";
import { DriveStore } from "../../store/reducers";
import { IDefect, TDefectAssetType } from "../../model/Defect";
import { Device } from "../../model/Device";

import {
    isFromAustralia,
    isFromCanada,
    isValidProvince,
    getDvirDeclarationString
} from "../../modules/inspections/utils/regionUtils";
export {
    CANADA_PROVINCES,
    Province,
    Country,
    isFromProvince,
    isFromAustralia,
    isFromCanada,
    isDeviceFromOntario,
    isValidProvince
} from "../../modules/inspections/utils/regionUtils";

const DEFAULT_VEHICLE_DEFECTS_LIST_NAME = "Default Vehicle Defects List";
const DEFAULT_TRAILER_DEFECTS_LIST_NAME = "Default Trailer Defects List";
// Contract copy of the built-in inspection list titles owned by MyGeotab. Drive cannot import from MyGeotab.
const BUILT_IN_INSPECTION_LIST_TITLES = [
    "Schedule 1 - Truck, Tractor & Trailer",
    "Schedule 2 - Bus",
    "Schedule 3 - Motor Coach"
] as const;
const TRANSLATABLE_DEFECT_LIST_NAMES: ReadonlySet<string> = new Set([
    DEFAULT_VEHICLE_DEFECTS_LIST_NAME,
    DEFAULT_TRAILER_DEFECTS_LIST_NAME,
    ...BUILT_IN_INSPECTION_LIST_TITLES
]);

export function getDeviceLicense(
    store: DriveStore,
    device?: IIdEntity
): string {
    let vehicle = device || getCurrentVehicle();
    let deviceFromCache = vehicle
        ? makeDeviceCache(store).get({ id: vehicle.id })[0]
        : undefined;
    return deviceFromCache?.licenseState || "";
}

export function canSkipCertify(store: DriveStore, device?: IIdEntity) {
    let licenseState = getDeviceLicense(store, device);
    return licenseState
        ? isFromCanada(licenseState) || isFromAustralia(licenseState)
        : false;
}

export function formInspectedVehicleString(
    translator: Translator,
    model: DVIRLog,
    defectList: IDefect,
    store: DriveStore,
    language?: string,
    isInspecting?: boolean
): Promise<string> {
    let resourceType = model.getResourceType();
    let isDevice = resourceType === "device";
    let licenseState = model.device.licenseState;

    if (!isDevice && model._device) {
        let cachedTrailerVehicle = makeDeviceCache(store).get(
            model._device
        )[0] as Device;
        licenseState =
            cachedTrailerVehicle && cachedTrailerVehicle.licenseState;
    }

    let region =
        licenseState && isValidProvince(licenseState)
            ? licenseState
            : model._region || "";
    let assetTypeString = isDevice ? "vehicle" : resourceType;
    const declarationString = isFromAustralia(region)
        ? (isInspecting ? "Are you sure the inspection of this {0} is complete?" : "")
        : getDvirDeclarationString(region);
    return translator
        .translate([assetTypeString, declarationString], language)
        .then((translatedText: string[]) => {
            let assetTypeStringTranslated = translatedText[0];
            let declarationStringTranslated = translatedText[1];
            if (defectList) {
                return declarationStringTranslated
                    .replace("{0}", assetTypeStringTranslated)
                    .replace("{1}", defectList.name);
            } else {
                return declarationStringTranslated.replace(
                    "{0}",
                    assetTypeStringTranslated
                );
            }
        });
}

export function translateDefectListName(
    name: string,
    translator: Translator,
    language?: string
): Promise<string> {
    let getDefectDescription = (listName: string) => {
        switch (listName) {
            case "** Default Vehicle Defects **":
                return DEFAULT_VEHICLE_DEFECTS_LIST_NAME;
            case "** Default Trailer Defects **":
                return DEFAULT_TRAILER_DEFECTS_LIST_NAME;
            default:
                return listName;
        }
    };

    let defectListName = getDefectDescription(name);
    // Translator.translate never rejects (it resolves to the untranslated form on
    // failure), so only built-in/system names need a lookup; everything else — including
    // custom user-entered list names — is returned verbatim without a translation attempt.
    return TRANSLATABLE_DEFECT_LIST_NAMES.has(defectListName)
        ? translator.translate(defectListName, language)
        : Promise.resolve(defectListName);
}

function getPopulatedDefectHash(
    api: Api,
    store: DriveStore,
    assetType: TDefectAssetType,
    includeParent = false
): Promise<IIdDictionary<IDefect>> {
    return getDefectListsForAssetType(api, store, assetType).then(
        (defectLists) => {
            let allDefects: IIdDictionary<IDefect> = {};
            defectLists.forEach((defectGroup) => {
                let parts = entityUtil.populateParentNodes(
                    defectGroup.children || [],
                    includeParent ? defectGroup : null
                );
                Object.keys(parts).forEach((partId) => {
                    allDefects[partId] = parts[partId] as IDefect;
                });
            });
            return allDefects;
        }
    );
}

export function getPopulatedDefects(api: Api, store: DriveStore, dvirDefects: IDvirDefects[], assetType: TDefectAssetType): Promise<IDvirDefects[]> {
    return getPopulatedDefectHash(api, store, assetType)
        .then(defectHash => {
            const result: IDvirDefects[] = [];
            dvirDefects.forEach(defect => {
                if (defectHash.hasOwnProperty(defect.defect.id)) {
                    // Mutate defect.defect in place so that callers holding a reference to the
                    // original defect object (e.g. getGroupedDefects uses the raw defects array)
                    // also see the hydrated name and parent chain.
                    defect.defect = JSON.parse(JSON.stringify(defectHash[defect.defect.id]));
                } else {
                    // The defect's original defect list is outside the user's current scope
                    // (e.g. group access was removed after the defect was reported).
                    // Include it as-is so the user can still update the repair status of the
                    // carried-over defect. The defect name will be blank in this edge case.
                    // DefectBridge.FromIdAsync is not supported so we cannot fetch the name by ID.
                    // See MYG-135056.
                }
                result.push(defect);
            });
            return result;
        });
}

export function getDefectListForDefect(
    api: Api,
    store: DriveStore,
    defect: IIdEntity,
    assetType: TDefectAssetType
): Promise<IDefect | null> {
    return getPopulatedDefectHash(api, store, assetType, true).then(
        (defectHash) => {
            if (!defectHash.hasOwnProperty(defect.id)) {
                return null;
            }
            let parentDefect = defectHash[defect.id];
            do {
                parentDefect = parentDefect.parent as IDefect;
            } while (parentDefect.parent);
            return parentDefect;
        }
    );
}

export function getGroupedDefects(
    populatedDefects: IDvirDefects[],
    defects: IDvirDefects[],
    getDefectPathName: (defect: IDefect) => string
) {
    let newDefects = populatedDefects;
    newDefects = defects.map((defect) => {
        (defect.defect as IDefect).name = getDefectPathName(
            defect.defect as IDefect
        );
        return defect;
    });

    let defectSort = (defectA: IDefect, defectB: IDefect) =>
        (defectA?.name || "") < (defectB?.name || "") ? -1 : 1;
    let dvirDefectSort = (
        dvirDefectA: IDvirDefects,
        dvirDefectB: IDvirDefects
    ) =>
        ((dvirDefectA?.defect as IDefect)?.name || "") <
        ((dvirDefectB?.defect as IDefect)?.name || "")
            ? -1
            : 1;

    let parentDefects: IDefect[] = newDefects.map((defect) => {
        if ((defect.defect as IDefect).parent) {
            return (defect.defect as IDefect).parent as IDefect;
        }
        return defect.defect as IDefect;
    });
    parentDefects.sort(defectSort);

    let groupedDefects: { [id: string]: IDvirDefects[] } = {};
    parentDefects.forEach((parentDefect) => {
        groupedDefects[parentDefect.id] = [];
    });

    let partsDefects: IDvirDefects[] = [];
    defects.forEach((defect) => {
        let defectParentId =
            defect.defect && (defect.defect as IDefect).parent?.id;
        if (defectParentId) {
            groupedDefects[defectParentId].push(defect);
        } else {
            partsDefects.push(defect);
        }
    });

    Object.keys(groupedDefects).forEach((parentDefectId) => {
        groupedDefects[parentDefectId].sort(dvirDefectSort);
    });

    partsDefects.forEach((defect) => {
        groupedDefects[defect.defect.id].push(defect);
    });

    return groupedDefects;
}

export function getDefectNames(
    translator: Translator,
    api: Api,
    store: DriveStore,
    defects: IDvirDefects[],
    assetType: TDefectAssetType,
    language?: string,
    populatedDefectNames?: { [id: string]: string }
): Promise<{ [id: string]: IDvirDefects[] }> {
    let defectsTask = getPopulatedDefects(api, store, defects, assetType);

    return new Promise<{ [id: string]: IDvirDefects[] }>((resolve) => {
        let defectListTranslation = new DefectListTranslation(
            translator,
            language
        );
        defectListTranslation.ready(() => {
            let getDefectNamesArray = (group: IDefect): string[] => {
                let baseDefectArray =
                    group && group.parent && group.parent.name
                        ? getDefectNamesArray(group.parent)
                        : [];
                baseDefectArray.push(group.name);
                return baseDefectArray;
            };
            let getDefectPathName = (defect: IDefect) => {
                let defectNamesArray = getDefectNamesArray(defect);
                let translations: string[] = [];

                defectNamesArray.forEach((defectToTranslate) => {
                    translations.push(
                        defectListTranslation.translate(defectToTranslate)
                    );
                });

                // MYG-135056: when a carried-over defect's grandparent category is not
                // fetched (includeParent = false), the ancestry chain is truncated to
                // [category, "Other"]. In that case show the category name instead of
                // the generic "Other" leaf so the label stays meaningful.
                const leafIsGenericOther =
                    defectNamesArray.length > 1 && defectNamesArray[1] === "Other";
                let baseDefectPath = htmlEscape(
                    leafIsGenericOther ? translations[0] : (translations[1] || translations[0])
                );
                if (translations[0] && defect.parent) {
                    defect.parent.name = htmlEscape(translations[0]);
                }

                if (populatedDefectNames) {
                    populatedDefectNames[defect.id] =
                        `${ translations[0] }${ translations[1] ? ` → ${ translations[1] }` : "" }`;
                }
                return baseDefectPath;
            };

            return defectsTask.then((populatedDefects) => {
                if (populatedDefects && populatedDefects.length) {
                    // An array was passed in, so return an array
                    // Pass populatedDefects (not the original defects) so getGroupedDefects
                    // receives the enriched defect objects with parent info from the hash or
                    // fetched by ID. The original defects array is no longer mutated in place.
                    let groupedDefects = getGroupedDefects(populatedDefects, populatedDefects, getDefectPathName);
                    resolve(groupedDefects);
                } else {
                    resolve({});
                }
            });
        });
    });
}

export function getLabel(severity: string | undefined) {
    let severityLevel = severity && severity.toLowerCase();

    if (severityLevel === "critical") {
        return "<span class=\"tag inspection\">MAJOR</span>";
    }

    if (severityLevel === "unregulated") {
        return "<span class=\"tag inspection unregulated\">UNREGULATED</span>";
    }
    return "";
}

